import { useState, useRef, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { Checkbox } from 'primereact/checkbox';
import bgImage from '../assets/images/new_bgs.png';
import logoImage from '../assets/images/payPlus/payPlusNoBackground.png';
import logoImageWithTitle from '../assets/images/payPlus/payPlusWithTitle.png';
import axiosInstance from '../utils/axiosInstance';
import StateSelector from '../components/StateSelector';
import InputText from '../components/InputText';
import KVKKModal from '../components/Contracts/KVKKModal';
import PrivacyModal from '../components/Contracts/PrivacyModal';
import InformationModal from '../components/Contracts/InformationModal';
import WarningArea from '../components/WarningArea';

const Register = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useRef(null);

  const [kvkkModalVisible, setKvkkModalVisible] = useState(false);
  const [privacyModalVisible, setPrivacyModalVisible] = useState(false);
  const [informationModalVisible, setInformationModalVisible] = useState(false);

  useEffect(() => {
    localStorage.setItem('visited_count', '1');
  }, []);

  const validationSchema = Yup.object({
    name: Yup.string().required(t('nameRequired')),
    surname: Yup.string().required(t('surnameRequired')),
    username: Yup.string().required(t('usernameRequired')),
    email: Yup.string().email(t('invalidEmail')).required(t('emailRequired')),
    password: Yup.string()
      .min(8, t('passwordMinLength'))
      .matches(/[a-z]/, t('passwordLowercase'))
      .matches(/[A-Z]/, t('passwordUppercase'))
      .matches(/[0-9]/, t('passwordNumber'))
      .matches(/[^a-zA-Z0-9]/, t('passwordSpecial'))
      .required(t('passwordRequired')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], t('passwordsMatch'))
      .required(t('confirmPasswordRequired')),
    identityNumber: Yup.string()
      .matches(/^[0-9]{11}$/, t('identityNumberFormat'))
      .required(t('identityNumberRequired')),
    vkn: Yup.string()
      .matches(/^[1-9][0-9]{9,10}$/, 'VKN 10-11 haneli olmalı ve 0 ile başlayamaz')
      .required(t('vknRequired')),
    phoneNumber1: Yup.string()
      .test('phone-format', 'Telefon numarası 5 ile başlamalı', function (value) {
        if (!value) return false;
        const cleanPhone = value.replace(/[^\d]/g, '');
        const phoneWithoutCountry = cleanPhone.startsWith('0') ? cleanPhone.slice(1) : cleanPhone;
        return /^5[0-9]{9}$/.test(phoneWithoutCountry);
      })
      .required(t('phoneNumberRequired')),
    phoneNumber2: Yup.string()
      .test('phone-format-optional', 'Telefon numarası 5 ile başlamalı', function (value) {
        if (!value || value === '') return true;
        const cleanPhone = value.replace(/[^\d]/g, '');
        const phoneWithoutCountry = cleanPhone.startsWith('0') ? cleanPhone.slice(1) : cleanPhone;
        return /^5[0-9]{9}$/.test(phoneWithoutCountry);
      }),
    countryOfBirth: Yup.object().nullable().required(t('birthCountryRequired')),
    districtOfBirth: Yup.object().nullable().required(t('birthDistrictRequired')),
    birthday: Yup.mixed()
      .test('is-date', t('invalidDate'), function (value) {
        if (!value) return false;
        if (value instanceof Date && !isNaN(value.getTime())) return true;
        return false;
      })
      .required(t('birthdayRequired')),
    countryOfResidentialAddress: Yup.object().nullable().required(t('residentialCountryRequired')),
    districtOfResidentialAddress: Yup.object().nullable().required(t('residentialDistrictRequired')),
    detailOfResidentialAddress: Yup.string().required(t('residentialDetailRequired')),
    countryOfWorkAddress: Yup.object().nullable().required(t('workCountryRequired')),
    districtOfWorkAddress: Yup.object().nullable().required(t('workDistrictRequired')),
    detailOfWorkAddress: Yup.string().required(t('workDetailRequired')),
    kvkk: Yup.boolean().oneOf([true], t('kvkkRequired')),
    privacyAgreement: Yup.boolean().oneOf([true], t('privacyRequired')),
    informationAgreement: Yup.boolean().oneOf([true], t('informationRequired'))
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      surname: '',
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      identityNumber: '',
      vkn: '',
      phoneNumber1: '',
      phoneNumber2: '',
      countryOfBirth: null,
      districtOfBirth: null,
      birthday: null,
      countryOfResidentialAddress: null,
      districtOfResidentialAddress: null,
      detailOfResidentialAddress: '',
      countryOfWorkAddress: null,
      districtOfWorkAddress: null,
      detailOfWorkAddress: '',
      kvkk: false,
      privacyAgreement: false,
      informationAgreement: false,
      companyName: ''
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const cleanPhone1 = values.phoneNumber1.replace(/[^\d]/g, '');
        const phone1 = cleanPhone1.startsWith('0') ? cleanPhone1.slice(1) : cleanPhone1;

        const cleanPhone2 = values.phoneNumber2 ? values.phoneNumber2.replace(/[^\d]/g, '') : null;
        const phone2 = cleanPhone2 ? (cleanPhone2.startsWith('0') ? cleanPhone2.slice(1) : cleanPhone2) : null;

        const userData = {
          name: values?.name,
          surname: values?.surname,
          username: values?.username,
          password: values?.password,
          email: values?.email,
          phoneNumber1: phone1,
          phoneNumber2: phone2,
          countryOfBirth: values?.countryOfBirth?.il,
          districtOfBirth: values?.districtOfBirth?.ilce,
          countryOfResidentialAddress: values?.countryOfResidentialAddress?.il,
          districtOfResidentialAddress: values?.districtOfResidentialAddress?.ilce,
          detailOfResidentialAddress: values?.detailOfResidentialAddress,
          countryOfWorkAddress: values?.countryOfWorkAddress?.il,
          districtOfWorkAddress: values?.districtOfWorkAddress?.ilce,
          detailOfWorkAddress: values?.detailOfWorkAddress,
          birthday: values?.birthday ? values?.birthday.toISOString() : null,
          identityNumber: values?.identityNumber,
          vkn: values?.vkn,
          companyName: values?.companyName,
          kvkk: values?.kvkk ? 'Evet' : 'Hayır',
          privacyAgreement: values?.privacyAgreement ? 'Evet' : 'Hayır',
          informationAgreement: values?.informationAgreement ? 'Evet' : 'Hayır',
        };

        await axiosInstance.post('/api/v1/users/register', userData);

        toast.current.show({
          severity: 'success',
          summary: t('success'),
          detail: t('registerSuccess'),
          life: 3000
        });

        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } catch (error) {
        console.error('Registration error:', error);
        toast.current.show({
          severity: 'error',
          summary: t('error'),
          detail: error.response?.data?.message || t('registerError'),
          life: 3000
        });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const handleKvkkApprove = () => {
    formik.setFieldValue('kvkk', true);
  };

  const handlePrivacyApprove = () => {
    formik.setFieldValue('privacyAgreement', true);
  };

  const handleInformationApprove = () => {
    formik.setFieldValue('informationAgreement', true);
  };

  return (
    <div className="relative h-screen w-screen overflow-hidden">
      <Toast ref={toast} />

      <KVKKModal
        visible={kvkkModalVisible}
        onHide={() => setKvkkModalVisible(false)}
        onApprove={handleKvkkApprove}
      />

      <PrivacyModal
        visible={privacyModalVisible}
        onHide={() => setPrivacyModalVisible(false)}
        onApprove={handlePrivacyApprove}
      />

      <InformationModal
        visible={informationModalVisible}
        onHide={() => setInformationModalVisible(false)}
        onApprove={handleInformationApprove}
      />

      <div
        className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 dark:filter dark:grayscale-[70%]"
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      />
      <div className="relative flex gap-0 md:gap-10 p-5 md:p-10 h-full">
        {/* Left Section - Logo and Image */}
        <div className="hidden md:flex h-[90vh] w-[50vw] bg-white bg-opacity-50 justify-center items-center rounded-3xl">
          <div className="h-[90%] w-[90%] flex justify-start items-center bg-white bg-opacity-80 p-2 rounded-3xl relative shadow-2xl shadow-gray-500">
            <img
              className='dark:filter dark:grayscale-[70%]'
              src={logoImageWithTitle}
              alt="Logo"
            />
          </div>
        </div>

        {/* Right Section - Registration Form */}
        <div className="h-[90vh] w-full md:w-[50vw] bg-white bg-opacity-50 p-3 md:p-5 flex justify-center items-center rounded-3xl overflow-auto">
          <div className="bg-white bg-opacity-90 rounded-3xl p-2 shadow-2xl shadow-gray-500 h-[85vh] w-full md:w-[90%] max-w-4xl overflow-y-auto">
            <form onSubmit={formik.handleSubmit} className="flex flex-col">
              <div className="flex justify-center">
                <img src={logoImage} alt="Logo" className="h-[8vh] md:h-[10vh] w-[12vw] md:w-[8vw] object-contain dark:filter dark:grayscale-[70%]" />
              </div>
              <div className="text-center flex flex-col items-center gap-y-2 py-2">
                <h1 className="font-firaSans font-bold text-xl md:text-2xl text-logo-darkest">
                  {t('createAcc')}
                </h1>
                <p className="text-xs md:text-sm text-logo-dark">{t('createAccountDescription')}</p>
              </div>

              <div className="mt-2 flex justify-end px-5 md:px-10">
                <p className="text-red-600 dark:text-red-400 font-semibold italic text-xs md:text-sm">* {t('requiredFields')}</p>
              </div>

              <WarningArea type='warning' message="Lütfen tüm bilgilerin eksiksiz ve doğru şekilde girildiğinden emin olunuz. Kullanıcı kaynaklı hatalardan doğabilecek maddi kayıplardan PayPlus sorumluluk kabul etmemektedir." />

              <div className="mb-5 flex flex-col gap-y-3 md:gap-y-4 p-3 md:p-5 pt-4">
                {/* Personal Information */}
                <h3 className="font-medium text-base md:text-lg">{t('personalInfo')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <InputText
                    camelCase={true}
                    id="name"
                    label={t('name')}
                    name="name"
                    type="text"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    clearFunction={() => formik.setFieldValue("name", "")}
                    error={formik.errors.name}
                    touched={formik.touched.name}
                    required
                  />

                  <InputText
                    camelCase={true}
                    id="surname"
                    label={t('surname')}
                    name="surname"
                    type="text"
                    value={formik.values.surname}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    clearFunction={() => formik.setFieldValue("surname", "")}
                    error={formik.errors.surname}
                    touched={formik.touched.surname}
                    required
                  />


                  <InputText
                    id="identityNumber"
                    label={t('identityNumber')}
                    name="identityNumber"
                    type="text"
                    value={formik.values.identityNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    clearFunction={() => formik.setFieldValue("identityNumber", "")}
                    error={formik.errors.identityNumber}
                    touched={formik.touched.identityNumber}
                    required
                  />

                  <InputText
                    id="vkn"
                    label={t('vkn')}
                    name="vkn"
                    type="text"
                    value={formik.values.vkn}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    clearFunction={() => formik.setFieldValue("vkn", "")}
                    error={formik.errors.vkn}
                    touched={formik.touched.vkn}
                    required
                  />
                </div>
                {/* Account Information */}
                <h3 className="font-medium text-base md:text-lg mt-2">{t('accountInfo')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <InputText
                    id="username"
                    label={t('username')}
                    name="username"
                    type="text"
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    clearFunction={() => formik.setFieldValue("username", "")}
                    error={formik.errors.username}
                    touched={formik.touched.username}
                    required
                  />

                  <InputText
                    id="email"
                    label={t('mail')}
                    name="email"
                    type="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    clearFunction={() => formik.setFieldValue("email", "")}
                    error={formik.errors.email}
                    touched={formik.touched.email}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <InputText
                    id="password"
                    label={t('password')}
                    name="password"
                    type="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.errors.password}
                    touched={formik.touched.password}
                    required
                  />

                  <InputText
                    id="confirmPassword"
                    label={t('confirmPassword')}
                    name="confirmPassword"
                    type="password"
                    showTemplate={false}
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.errors.confirmPassword}
                    touched={formik.touched.confirmPassword}
                    feedback={false}
                    required
                  />
                </div>

                {/* Contact Information */}
                <h3 className="font-medium text-base md:text-lg mt-2">{t('contactInfo')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <InputText
                    id="phoneNumber1"
                    label={t('phone')}
                    name="phoneNumber1"
                    type="phone"
                    value={formik.values.phoneNumber1}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    clearFunction={() => formik.setFieldValue("phoneNumber1", "")}
                    error={formik.errors.phoneNumber1}
                    touched={formik.touched.phoneNumber1}
                    required
                  />

                  <InputText
                    id="phoneNumber2"
                    label={t('phone2')}
                    name="phoneNumber2"
                    type="phone"
                    value={formik.values.phoneNumber2}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    clearFunction={() => formik.setFieldValue("phoneNumber2", "")}
                    error={formik.errors.phoneNumber2}
                    touched={formik.touched.phoneNumber2}
                  />
                </div>

                {/* Birth Information */}
                <h3 className="font-medium text-base md:text-lg mt-2">{t('birthInfo')}</h3>
                <StateSelector
                  formik={formik}
                  labelIl={t('birthCountry')}
                  labelIlce={t('birthDistrict')}
                  ilFieldName="countryOfBirth"
                  ilceFieldName="districtOfBirth"
                  required
                />
                <InputText
                  id="birthday"
                  label={t('birthday')}
                  name="birthday"
                  type="date"
                  value={formik.values.birthday}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  clearFunction={() => formik.setFieldValue("birthday", null)}
                  error={formik.errors.birthday}
                  touched={formik.touched.birthday}
                  required
                />

                {/* Residential Address */}
                <h3 className="font-medium text-base md:text-lg mt-2">{t('residentialAddress')}</h3>
                <StateSelector
                  formik={formik}
                  labelIl={t('residentialCountry')}
                  labelIlce={t('residentialDistrict')}
                  ilFieldName="countryOfResidentialAddress"
                  ilceFieldName="districtOfResidentialAddress"
                  required
                />
                <WarningArea type='info' message="Lütfen adres detay bilgilerinizi örnekte belirtildiği şekilde eksiksiz ve doğru olarak giriniz. Senet çıktılarınızdada il ve ilçe bilgisi, ilgili seçim alanlarından otomatik olarak alınmaktadır. Örnek adres formatı: Murtaza Mahallesi Hacı Bülbül Sokak Örnek Apartmanı No:13/5" />
                <InputText
                  camelCase={true}
                  type="textarea"
                  label={t('residentialDetail')}
                  name="detailOfResidentialAddress"
                  value={formik.values.detailOfResidentialAddress}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  clearFunction={() => formik.setFieldValue("detailOfResidentialAddress", "")}
                  error={formik.errors.detailOfResidentialAddress}
                  touched={formik.touched.detailOfResidentialAddress}
                  required
                />

                {/* Work Address */}
                <h3 className="font-medium text-base md:text-lg mt-2">{t('workAddress')}</h3>
                <InputText
                  id="companyName"
                  label={t('companyName')}
                  name="companyName"
                  type="text"
                  value={formik.values.companyName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  clearFunction={() => formik.setFieldValue("companyName", "")}
                  error={formik.errors.companyName}
                  touched={formik.touched.companyName}
                  required
                />
                <StateSelector
                  formik={formik}
                  labelIl={t('workCountry')}
                  labelIlce={t('workDistrict')}
                  ilFieldName="countryOfWorkAddress"
                  ilceFieldName="districtOfWorkAddress"
                  required
                />
                <WarningArea type='info' message="Lütfen adres detay bilgilerinizi örnekte belirtildiği şekilde eksiksiz ve doğru olarak giriniz. Senet çıktılarınızdada il ve ilçe bilgisi, ilgili seçim alanlarından otomatik olarak alınmaktadır. Örnek adres formatı: Murtaza Mahallesi Hacı Bülbül Sokak Örnek Apartmanı No:13/5" />
                <InputText
                  camelCase={true}
                  type="textarea"
                  label={t('workDetail')}
                  name="detailOfWorkAddress"
                  value={formik.values.detailOfWorkAddress}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  clearFunction={() => formik.setFieldValue("detailOfWorkAddress", "")}
                  error={formik.errors.detailOfWorkAddress}
                  touched={formik.touched.detailOfWorkAddress}
                  required
                />

                <h3 className="font-medium text-base md:text-lg mt-4">{t('agreementsAndConsents')}</h3>

                {/* KVKK Onayı */}
                <div className="flex items-start mt-4 p-3 md:p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <Checkbox
                    inputId="kvkk"
                    name="kvkk"
                    checked={formik.values.kvkk}
                    onChange={e => formik.setFieldValue('kvkk', e.checked)}
                    className={`mr-3 mt-1 ${formik.touched.kvkk && formik.errors.kvkk ? 'p-invalid' : ''}`}
                    disabled={!formik.values.kvkk}
                  />
                  <div className="flex-1">
                    <label htmlFor="kvkk" className="text-xs md:text-sm font-medium text-gray-700 cursor-pointer">
                      <button
                        type="button"
                        className="text-blue-600 hover:text-blue-800 underline font-semibold"
                        onClick={() => setKvkkModalVisible(true)}
                      >
                        Kişisel Verilerin Korunması Kanunu (KVKK) Aydınlatma Metni
                      </button>
                      'ni okudum ve anladım. Kişisel verilerimin işlenmesine onay veriyorum.
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    {formik.values.kvkk && (
                      <div className="flex items-center mt-2 text-green-600">
                        <i className="pi pi-check-circle mr-2"></i>
                        <span className="text-xs md:text-sm">KVKK metni onaylandı</span>
                      </div>
                    )}
                  </div>
                </div>
                {formik.touched.kvkk && formik.errors.kvkk && <small className="p-error mt-1 text-xs">{formik.errors.kvkk}</small>}

                {/* Gizlilik Sözleşmesi Onayı */}
                <div className="flex items-start mt-4 p-3 md:p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <Checkbox
                    inputId="privacyAgreement"
                    name="privacyAgreement"
                    checked={formik.values.privacyAgreement}
                    onChange={e => formik.setFieldValue('privacyAgreement', e.checked)}
                    className={`mr-3 mt-1 ${formik.touched.privacyAgreement && formik.errors.privacyAgreement ? 'p-invalid' : ''}`}
                    disabled={!formik.values.privacyAgreement}
                  />
                  <div className="flex-1">
                    <label htmlFor="privacyAgreement" className="text-xs md:text-sm font-medium text-gray-700 cursor-pointer">
                      <button
                        type="button"
                        className="text-orange-600 hover:text-orange-800 underline font-semibold"
                        onClick={() => setPrivacyModalVisible(true)}
                      >
                        Gizlilik Sözleşmesi ve Kullanım Koşulları
                      </button>
                      'nı okudum ve kabul ediyorum.
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    {formik.values.privacyAgreement && (
                      <div className="flex items-center mt-2 text-green-600">
                        <i className="pi pi-check-circle mr-2"></i>
                        <span className="text-xs md:text-sm">Gizlilik sözleşmesi kabul edildi</span>
                      </div>
                    )}
                  </div>
                </div>
                {formik.touched.privacyAgreement && formik.errors.privacyAgreement && <small className="p-error mt-1 text-xs">{formik.errors.privacyAgreement}</small>}

                {/* Aydınlatma Metni Onayı */}
                <div className="flex items-start mt-4 p-3 md:p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <Checkbox
                    inputId="informationAgreement"
                    name="informationAgreement"
                    checked={formik.values.informationAgreement}
                    onChange={e => formik.setFieldValue('informationAgreement', e.checked)}
                    className={`mr-3 mt-1 ${formik.touched.informationAgreement && formik.errors.informationAgreement ? 'p-invalid' : ''}`}
                    disabled={!formik.values.informationAgreement}
                  />
                  <div className="flex-1">
                    <label htmlFor="informationAgreement" className="text-xs md:text-sm font-medium text-gray-700 cursor-pointer">
                      <button
                        type="button"
                        className="text-purple-600 hover:text-purple-800 underline font-semibold"
                        onClick={() => setInformationModalVisible(true)}
                      >
                        Aydınlatma Metni ve Hizmet Koşulları
                      </button>
                      'ni okudum ve onaylıyorum.
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    {formik.values.informationAgreement && (
                      <div className="flex items-center mt-2 text-green-600">
                        <i className="pi pi-check-circle mr-2"></i>
                        <span className="text-xs md:text-sm">Aydınlatma metni onaylandı</span>
                      </div>
                    )}
                  </div>
                </div>
                {formik.touched.informationAgreement && formik.errors.informationAgreement && <small className="p-error mt-1 text-xs">{formik.errors.informationAgreement}</small>}

                {/* Submit Button */}
                <Button
                  type="submit"
                  label={t('signup')}
                  loading={formik.isSubmitting}
                  className="bg-logo-mid hover:bg-logo-dark dark:bg-gray-500 dark:hover:bg-gray-600 py-4 md:py-5 text-white font-medium rounded-lg shadow-lg mt-4"
                />

                {/* Additional Actions */}
                <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-4 mt-4">
                  <p
                    onClick={() => navigate('/login')}
                    className="text-center text-logo-dark font-semibold hover:underline hover:text-logo-darkest text-sm md:text-base cursor-pointer"
                  >
                    {t('haveAccount')}
                  </p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style jsx>{`
        :global(.p-float-label) {
          width: 100%;
        }
        
        :global(.p-float-label input:focus) ~ label,
        :global(.p-float-label input.p-filled) ~ label {
          top: -0.75rem;
          font-size: 12px;
          background-color: #ffffff;
          padding: 2px 4px;
          border-radius: 3px;
        }
        
        :global(.dark) .p-float-label input:focus ~ label,
        :global(.dark) .p-float-label input.p-filled ~ label {
          background-color: #2d3748;
          color: #60a5fa;
        }
        
        :global(.p-password-panel) {
          width: 100%;
        }
        
        :global(.dark) .p-password-panel {
          background-color: #1e293b !important;
          color: #e2e8f0 !important;
          border-color: #475569 !important;
        }
      `}</style>
    </div>
  );
};

export default Register;